import {
  MutedFilled,
  PauseCircleFilled,
  PlayCircleFilled,
  SoundFilled,
} from '@ant-design/icons';
import { Alert } from 'antd';
import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from '@refinedev/core';

interface CustomAudioPlayerProps {
  className?: string;
  includeImage?: boolean;
  audioUrl: string;
  date?: string;
  category?: string;
}

export const CustomAudioPlayer: React.FC<CustomAudioPlayerProps> = ({
  className,
  includeImage,
  audioUrl,
  date,
  category,
}) => {
  const { translate } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current;
      const handleLoadedMetadata = () => {
        if (isFinite(audio.duration) && !isNaN(audio.duration)) {
          setDuration(audio.duration);
          setIsLoaded(true);
        }
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
        setIsPlaying(true);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const formatTime = (time: number) => {
    if (!isFinite(time) || isNaN(time)) {
      return '--:--';
    }
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        setVolume(1);
        setIsMuted(false);
      } else {
        setVolume(0);
        setIsMuted(true);
      }
    }
  };

  return (
    <>
      {!audioError ? (
        <div className={`${className}`}>
          <div
            className={`audio-player-container flex flex-row justify-between items-center mx-auto gap-3 lg:gap-8 p-[0.94rem] bg-transparent ${
              includeImage
                ? 'bg-opacity-10 px-2 md:px-7 lg:px-8'
                : 'px-2 md:px-4 xl:px-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow'
            } rounded-2xl w-full`}
          >
            {(date || category) && (
              <div
                className={`flex flex-row justify-center items-center w-fit gap-2 md:gap-4 ${includeImage ? 'md:pe-10' : 'pe-1'}`}
              >
                {date && (
                  <h1
                    className={`py-1 px-2 md:py-3 md:px-5 rounded-full text-nowrap font-montserrat font-normal text-[0.6rem] lg:text-sm 2xl:text-[1rem] text-primary ${includeImage ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    {date}
                  </h1>
                )}
                {category && (
                  <li
                    className={`text-nowrap list-disc font-montserrat font-light ${includeImage ? 'text-white' : 'text-primary'} text-nowrap text-[0.6rem] md:text-sm xl:text-[1rem] 2xl:text-lg hover:text-accent transition-all`}
                  >
                    {category}
                  </li>
                )}
              </div>
            )}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio
              ref={audioRef}
              className="hidden"
              src={`${audioUrl}.mp3`}
              onError={() => setAudioError(true)}
              onTimeUpdate={handleTimeUpdate}
            >
              {/* <track
                kind="captions"
                srcLang="en"
                label="English captions"
                default
              /> */}
              Your browser does not support the audio element.
            </audio>

            <div className="audio-progress w-full md:pt-3">
              <input
                type="range"
                className="w-full rotate-180 appearance-none bg-gray-100 h-[5px]"
                value={currentTime}
                max={duration}
                onChange={(e) => {
                  if (audioRef.current) {
                    audioRef.current.currentTime = Number(e.target.value);
                  }
                }}
                style={{
                  background: `linear-gradient(to left, ${includeImage ? '#ffffff 0%, #ffffff' : '#C5C386 0%, #C5C386'} ${(currentTime / duration) * 100}%, #D1D5DB ${(currentTime / duration) * 100}%, #DCDBDB 100%)`,
                }}
              />
              <div className={`flex justify-between w-full items-center `}>
                <div className="text-xs md:text-sm ">
                  {isLoaded ? formatTime(duration) : '--:--'}
                </div>
                <div className="text-xs md:text-sm">
                  {isLoaded ? formatTime(currentTime) : '--:--'}
                </div>
              </div>
            </div>

            <button
              className=" text-blue-500 hover:text-slate-600 transition-all "
              onClick={togglePlay}
            >
              {isPlaying ? (
                <PauseCircleFilled
                  // size={40}
                  className="hover:text-slate-600 transition-all text-3xl"
                />
              ) : (
                <PlayCircleFilled
                  // size={40}
                  className="hover:text-slate-600 transition-all text-3xl"
                />
              )}
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleMute}
                className="text-blue-500 hover:text-slate-600 transition-all"
              >
                {isMuted || volume === 0 ? <MutedFilled /> : <SoundFilled />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="w-20 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, ${includeImage ? '#ffffff' : '#C5C386'} ${volume * 100}%, #D1D5DB ${volume * 100}%)`,
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <Alert message={translate('messages.missing-media')} type="error" />
      )}
    </>
  );
};
